import { useState, useRef, useEffect } from "react";
import { Star, ExternalLink, Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { COMPANY } from "../../lib/constants";
import { useInView, revealClass } from "../../hooks/useInView";

const REVIEWS = [
  {
    name: "Sabine K.",
    when: "vor 2 Wochen",
    stars: 5,
    color: "#EA4335", // Google red
    text:
      "Super schneller Service! Habe per WhatsApp ein Foto vom Schrott im Keller geschickt und am nächsten Tag war alles weg. Kostenlos und freundlich. Klare Empfehlung.",
    tag: "Schrottabholung · Bremen-Nord",
  },
  {
    name: "Thomas R.",
    when: "vor 1 Monat",
    stars: 5,
    color: "#4285F4", // Google blue
    text:
      "Komplette Haushaltsauflösung nach dem Tod meines Vaters. Diskret, pünktlich, fair im Preis. Übergabe war wirklich besenrein – wie versprochen. Vielen Dank!",
    tag: "Haushaltsauflösung",
  },
  {
    name: "Anna L.",
    when: "vor 3 Wochen",
    stars: 5,
    color: "#FBBC05", // Google yellow
    text:
      "Festpreis-Angebot kam innerhalb von 24h. Team war freundlich, schnell und sauber. Garage und Dachboden komplett entrümpelt. Top!",
    tag: "Entrümpelung · Garage",
  },
  {
    name: "Mehmet Y.",
    when: "vor 2 Monaten",
    stars: 5,
    color: "#34A853", // Google green
    text:
      "Habe alte Heizkörper und Metallreste abholen lassen. 0 € Anfahrt, alles wie besprochen, sehr nette Jungs. Mache es wieder.",
    tag: "Schrottabholung · Walle",
  },
  {
    name: "Petra H.",
    when: "vor 1 Woche",
    stars: 5,
    color: "#EA4335",
    text:
      "Hatte einen kurzfristigen Termin – am selben Tag erledigt! Eingespieltes Team, faire Preise, kein Stress. Genau das, was ich gebraucht habe.",
    tag: "Express · Findorff",
  },
  {
    name: "Klaus M.",
    when: "vor 1 Monat",
    stars: 5,
    color: "#4285F4",
    text:
      "Sehr professionell und zuverlässig. Wohnungsauflösung in der Neustadt, alles in einem Tag erledigt. Preis hat genau gestimmt, keine versteckten Kosten.",
    tag: "Wohnungsauflösung",
  },
];

export const Testimonials = () => {
  const [headRef, headIn] = useInView();
  const scrollerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.clientWidth + 16 : 360;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setProgress(max <= 0 ? 1 : el.scrollLeft / max);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <section
      id="bewertungen"
      data-testid="testimonials-section"
      className="relative py-16 md:py-24 bg-white border-t border-black/8 overflow-hidden"
    >
      {/* Google color accents top */}
      <div className="absolute top-0 left-0 right-0 h-1 flex" aria-hidden>
        <span className="flex-1" style={{ background: "#4285F4" }} />
        <span className="flex-1" style={{ background: "#EA4335" }} />
        <span className="flex-1" style={{ background: "#FBBC05" }} />
        <span className="flex-1" style={{ background: "#34A853" }} />
      </div>
      <div className="px-4 md:px-8 lg:px-12">
        <div
          ref={headRef}
          {...revealClass(headIn)}
          className="grid lg:grid-cols-12 gap-8 mb-10 md:mb-12"
        >
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-black/45">
              // 04 — Was Kunden sagen
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[68px] font-semibold tracking-[-0.04em] text-black leading-[1.02]">
              Echte Stimmen.
              <br />
              <span className="text-black/35">Echte Bewertungen.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex gap-0.5">
                  {[
                    "#EA4335",
                    "#FBBC05",
                    "#34A853",
                    "#4285F4",
                    "#EA4335",
                  ].map((c, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5"
                      style={{ fill: c, color: c }}
                    />
                  ))}
                </div>
                <span className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                  4.9
                </span>
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/50">
                  / 5
                </span>
              </div>
              <a
                href={COMPANY.googleReviewUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="testimonials-google-link"
                className="mt-3 inline-flex items-center gap-2 text-sm text-black/70 hover:text-black underline underline-offset-4 decoration-black/30 hover:decoration-black"
              >
                Alle Bewertungen auf Google ansehen
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Arrow controls (desktop) */}
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                data-testid="testimonials-prev"
                aria-label="Vorherige Bewertung"
                className="w-12 h-12 rounded-full border border-black/15 bg-white hover:bg-black hover:text-white hover:border-black transition-all flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                data-testid="testimonials-next"
                aria-label="Nächste Bewertung"
                className="w-12 h-12 rounded-full border border-black/15 bg-white hover:bg-black hover:text-white hover:border-black transition-all flex items-center justify-center"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="relative -mx-4 md:-mx-8 lg:-mx-12">
          <div
            ref={scrollerRef}
            data-testid="testimonials-scroller"
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 px-4 md:px-8 lg:px-12 no-scrollbar"
          >
            {REVIEWS.map((r, i) => (
              <ReviewCard key={i} {...r} index={i} />
            ))}
          </div>

          {/* Fade edges */}
          <div className="hidden md:block absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent pointer-events-none" />
          <div className="hidden md:block absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none" />
        </div>

        {/* Progress bar */}
        <div className="mt-4 md:mt-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-black/10 relative overflow-hidden rounded-full">
            <div
              className="absolute left-0 top-0 bottom-0 bg-black rounded-full transition-all duration-300"
              style={{ width: `${Math.max(8, progress * 100)}%` }}
            />
          </div>
          <div className="md:hidden flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="testimonials-prev-mobile"
              aria-label="Vorherige Bewertung"
              className="w-10 h-10 rounded-full border border-black/15 bg-white flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="testimonials-next-mobile"
              aria-label="Nächste Bewertung"
              className="w-10 h-10 rounded-full border border-black/15 bg-white flex items-center justify-center"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ name, when, stars, text, tag, color, index }) => {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      data-card
      data-testid={`review-card-${index}`}
      className="group relative w-[85vw] sm:w-[420px] md:w-[420px] shrink-0 snap-start rounded-3xl p-6 md:p-7 border border-black/10 bg-white hover:border-black/30 transition-all duration-300 overflow-hidden"
    >
      {/* Color accent corner */}
      <div
        className="absolute top-0 left-0 w-24 h-1 rounded-br-full"
        style={{ background: color }}
        aria-hidden
      />
      <Quote
        className="absolute top-5 right-5 w-10 h-10 group-hover:opacity-30 transition-opacity"
        strokeWidth={1.2}
        style={{ color: color, opacity: 0.15 }}
      />

      <div className="flex items-center gap-1 mb-4 mt-2">
        {Array.from({ length: stars }).map((_, i) => (
          <Star
            key={i}
            className="w-3.5 h-3.5"
            style={{ fill: color, color: color }}
          />
        ))}
      </div>

      <p className="text-[15px] text-black/75 leading-relaxed min-h-[120px]">
        {text}
      </p>

      <div className="mt-6 pt-5 border-t border-black/8 flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full text-white font-display font-semibold text-sm flex items-center justify-center shrink-0"
          style={{ background: color }}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-black truncate">{name}</div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45 mt-0.5">
            {tag} · {when}
          </div>
        </div>
        <GoogleG className="ml-auto w-5 h-5 shrink-0" />
      </div>
    </div>
  );
};

const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
